import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/**
 * Get auth token for API requests with retry
 * Retries up to 3 times with 500ms delay if token is not immediately available
 * (handles race condition after OAuth redirect)
 */
async function getAuthHeaders(retries = 3): Promise<HeadersInit> {
  for (let i = 0; i < retries; i++) {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (token) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
    }

    // Wait before retry (except on last attempt)
    if (i < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  throw new Error('No authentication token available');
}

// ============================================
// Consent Types
// ============================================

export type PhotoVideoConsent = 'FULL' | 'BLURRED' | 'NO_PHOTO';

export interface UserConsents {
  photoVideoConsent: PhotoVideoConsent | null;
  photoVideoConsentAt: string | null;
  liabilityWaiverAgreed: boolean;
  liabilityWaiverAgreedAt: string | null;
  parentalReleaseAgreed: boolean;
  parentalReleaseAgreedAt: string | null;
}

export interface UpdateConsentsDTO {
  photoVideoConsent: PhotoVideoConsent;
  liabilityWaiverAgreed: boolean;
  parentalReleaseAgreed: boolean;
}

// ============================================
// Consent API
// ============================================

export const consentApi = {
  // Get user's current consent status
  getUserConsents: async (): Promise<UserConsents> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/consents`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user consents');
    }

    return response.json();
  },

  // Update user's consent agreements
  updateUserConsents: async (data: UpdateConsentsDTO): Promise<UserConsents> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/consents`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update user consents');
    }

    const result = await response.json();
    return result.data;
  },
};
